import { QueryKeys, useEntity, queryClient } from "@/api/core";
import { useAuth } from "@/contexts/auth/useAuth";
import { type Account, accountSchema } from "@/schemas";

import { useAccountRelationship } from "../../relationships";

interface UseAccountOpts {
  isEnabled?: boolean;
  refetch?: boolean;
  withRelationship?: boolean;
}

function useAccount(accountId?: string, opts: UseAccountOpts = {}) {
  const { currentAccount } = useAuth();
  const { refetch, withRelationship, isEnabled = true } = opts;

  const { queries } = queryClient.getDefaultOptions();

  const { data: account, ...result } = useEntity<Account>({
    queryKey: QueryKeys.accounts.account(accountId!),
    endpoint: `/api/v1/accounts/${accountId}`,
    schema: accountSchema,
    enabled: isEnabled && !!accountId,
    staleTime: refetch ? 0 : queries?.staleTime,
    cacheTime: refetch ? 0 : queries?.cacheTime,
    id: accountId!,
  });

  const {
    relationship,
    isLoading: isRelationshipLoading,
    isFetched: isRelationshipFetched,
  } = useAccountRelationship(accountId, {
    enabled: withRelationship && !!accountId && isEnabled,
  });

  const isBlocked = relationship?.blocked_by === true;
  const isUnavailable = currentAccount?.id === account?.id ? false : isBlocked;

  return {
    ...result,
    isLoading: result.isLoading || (withRelationship && isRelationshipLoading),
    isFetched: result.isFetched || (withRelationship && isRelationshipFetched),
    isUnavailable,
    account: account ? { ...account, relationship } : undefined,
  };
}

export { useAccount };
